import React, { useState } from "react";
import { HelpCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogTrigger, DialogTitle, DialogHeader, DialogDescription,
} from "@/components/ui/dialog";

const LABELS = {
  risk_adjusted_performance: "Risk-adjusted performance",
  pnl_consistency: "P&L consistency",
  category_performance: "Category performance",
  entry_price_quality: "Entry price quality",
  resolved_sample_size: "Resolved sample size",
  drawdown_control: "Drawdown control",
  recent_performance: "Recent performance",
  trader_quality: "Trader quality",
  consensus: "Consensus",
  conviction: "Conviction",
  remaining_edge: "Remaining edge",
  liquidity: "Liquidity",
  timing: "Timing",
  data_completeness: "Data completeness",
};

export default function ScoreExplanation({
  score,
  components = {},
  weights = {},
  missing = [],
  confidence,
  penalties = {},
  formulaVersion,
  label = "Why this score?",
  testid = "btn-score-explanation",
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={testid}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200 transition-colors"
        >
          <HelpCircle size={11} /> {label}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Score decomposition</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Transparent formula. Missing components are excluded and remaining weights are redistributed.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-3xl font-mono-num text-zinc-100">
              {score != null ? Math.round(score) : "—"}
            </div>
            {confidence != null && (
              <div className="text-[11px] text-zinc-500">
                Confidence: <span className="text-zinc-300 font-mono-num">{Math.round(confidence * 100)}%</span>
              </div>
            )}
          </div>
          <div className="border border-zinc-800 rounded-sm">
            {Object.entries(components).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-800/50 last:border-0">
                <div className="text-xs">
                  <div className="text-zinc-300">{LABELS[k] || k}</div>
                  <div className="text-[10px] text-zinc-600">weight {Math.round((weights[k] || 0) * 100)}%</div>
                </div>
                <div className="font-mono-num text-xs text-zinc-200">
                  {v == null ? <span className="text-zinc-600">unavailable</span> : Math.round(v)}
                </div>
              </div>
            ))}
          </div>
          {Object.keys(penalties).length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Penalties</div>
              {Object.entries(penalties).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs">
                  <span className="text-zinc-400">{(k || "").replaceAll("_", " ")}</span>
                  <span className="font-mono-num text-rose-400">−{Math.round(v)}</span>
                </div>
              ))}
            </div>
          )}
          {missing?.length > 0 && (
            <div className="mt-3 text-[11px] text-amber-300">
              Some components were unavailable and skipped:{" "}
              {missing.map((m) => LABELS[m] || m).join(", ")}.
            </div>
          )}
          {formulaVersion && (
            <div className="mt-3 text-[10px] text-zinc-600 font-mono-num">
              formula version {formulaVersion}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
